import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import * as _ from 'lodash';
import { ICompanySetting, ISetting, ISettingFilter } from "./settings.interface";
import { map } from 'rxjs/operators';
import { AuthService } from "../../../shared/services/auth.service";
import { UtilsService } from "../../../shared/services/utils.service";
import { HttpService } from "../../../../http.service";
import { apiURL, EnvConfig } from "../../../../env-config";

@Injectable()
export class SettingsService {
  constructor(
    private _http: HttpService,
    private _utilsService: UtilsService,
    private _AuthService: AuthService,
  ) { }

  getSettings(filter?: ISettingFilter): Observable<any> {
    let queryParams: string = !_.isEmpty(filter) ? '?' + this._utilsService.toQueryString(filter) : '';
    return this._http.get(`${apiURL(EnvConfig.API_SETTINGS)}${queryParams}`);
  }

  getCompanySettings(filter?: ISettingFilter): Observable<any> {
    let queryParams: string = !_.isEmpty(filter) ? '?' + this._utilsService.toQueryString(filter) : '';
    return this._http.get(`${apiURL(EnvConfig.API_SETTINGS)}/company${queryParams}`);
  }

  getSetting(setting: ISetting | string): Observable<any> {
    let id = (typeof setting == 'string') ? setting : setting._id;
    return this._http.get(`${apiURL(EnvConfig.API_SETTINGS)}/${id}`);
  }

  createSetting(setting: ISetting): Observable<any> {
    delete setting._id;
    return this._http.post(`${apiURL(EnvConfig.API_SETTINGS)}`, setting);
  }

  updateSetting(setting: ISetting): Observable<any> {
    let id = setting._id;
    delete setting._id;
    return this._http.patch(`${apiURL(EnvConfig.API_SETTINGS)}/${id}`, setting);
  }

  /* REFACTOR */
  /* cache values ??? */
  getCompanySetting(code: string) {
    return !code ? of(undefined) : this._getCompanySetting(code).pipe(
      map((setting: any) => setting.companySetting ? setting.companySetting.value : setting.defaultValue)
    );
  }

  private _getCompanySetting(code: string) {
    let query = {
      filters: { code: code },
      company: this._AuthService.companyId()
    };
    let queryParams: string = '?' + this._utilsService.toQueryString(query);
    return this._http.get(`${apiURL(EnvConfig.API_SETTINGS)}/code${queryParams}`);
  }

  getCompanySettingByFeature(filter?: ISettingFilter) {
    let queryParams: string = '?' + this._utilsService.toQueryString(filter);
    return this._http.get(`${apiURL(EnvConfig.API_SETTINGS)}/feature${queryParams}`);
  }

  async getCompanySettingValue(code: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this._getCompanySetting(code).subscribe((setting: any) => {
        if (setting) resolve(setting.companySetting ? setting.companySetting.value : setting.defaultValue);
        else resolve("")
      })
    });
  }

  async setCompanySettingValue(code: string, value: string) {
    return new Promise((resolve, reject) => {
      this._getCompanySetting(code).subscribe((setting: any) => {
        setting.value = value;
        //console.log(setting)
        const data: ICompanySetting = {
          _id: _.get(setting, 'companySetting._id'),
          company: _.get(setting, 'companySetting.company', this._AuthService.companyId()),
          setting: _.get(setting, '_id'),
          value: value
        };
        //console.log(data)
        this.setCompanySetting(data).subscribe((ok: any) => {
          resolve("");
        })
      })
    });
  }

  /* refactor */
  /* set by code + value, and company optional (if Admin or Dev) */
  setCompanySetting(companySetting: ICompanySetting): any {
    //console.log(companySetting)
    if (!companySetting._id) {
      if (companySetting.value === "") {
        return
      } else {
        return this._http.post(`${apiURL(EnvConfig.API_COMPANY_SETTINGS)}`, companySetting);
      }
    } else {
      let id = companySetting._id;
      delete companySetting._id;
      if (companySetting.value === "") {
        return this._http.delete(`${apiURL(EnvConfig.API_COMPANY_SETTINGS)}/${id}`, companySetting)
      } else {
        return this._http.patch(`${apiURL(EnvConfig.API_COMPANY_SETTINGS)}/${id}`, companySetting);
      }
    }
  }

  deleteSetting(setting: ISetting | string, hardDelete: boolean = false): Observable<void> {
    let id = (typeof setting == 'string') ? setting : setting._id;
    if (hardDelete) return this._http.delete(`${apiURL(EnvConfig.API_SETTINGS)}/${id}`);
    return this._http.delete(`${apiURL(EnvConfig.API_SETTINGS)}/${id}/delete`);
  }

  restoreSetting(setting: ISetting | string): Observable<void> {
    let id = (typeof setting == 'string') ? setting : setting._id;
    return this._http.patch(`${apiURL(EnvConfig.API_SETTINGS)}/${id}/restore`, null);
  }
}